// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { DiagnosticReport, DocumentReference, Patient, Practitioner, ServiceRequest } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { CLOUDPAT_HSAT_CODING } from '@polarissleep/shared';
import { useCallback, useEffect, useState } from 'react';

export interface HsatOrderRow {
  request: ServiceRequest;
  orderedBy: Practitioner | undefined;
  results: DocumentReference | undefined;
  diagnosticReport: DiagnosticReport | undefined;
}

export interface UsePatientHsatOrdersResult {
  orders: HsatOrderRow[];
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

/**
 * Loads HSAT orders for a patient. An order is any ServiceRequest with the
 * HSAT SNOMED code; we enrich each one with the ordering Practitioner and,
 * when present, the DocumentReference that carries its results.
 */
export function usePatientHsatOrders(patient: Patient | undefined): UsePatientHsatOrdersResult {
  const medplum = useMedplum();
  const [orders, setOrders] = useState<HsatOrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!patient?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const requests = (await medplum.searchResources('ServiceRequest', {
        subject: `Patient/${patient.id}`,
        code: `${CLOUDPAT_HSAT_CODING.system}|${CLOUDPAT_HSAT_CODING.code}`,
        _sort: '-authored',
        _count: '100',
      })) as ServiceRequest[];

      const rows = await Promise.all(
        Array.from(requests).map(async (request): Promise<HsatOrderRow> => {
          const [orderedBy, results, diagnosticReport] = await Promise.all([
            request.requester?.reference?.startsWith('Practitioner/')
              ? (medplum.readReference({ reference: request.requester.reference }).catch(() => undefined) as Promise<
                  Practitioner | undefined
                >)
              : Promise.resolve(undefined),
            medplum
              .searchOne('DocumentReference', {
                subject: `Patient/${patient.id}`,
                related: `ServiceRequest/${request.id}`,
              })
              .catch(() => undefined) as Promise<DocumentReference | undefined>,
            medplum
              .searchOne('DiagnosticReport', {
                'based-on': `ServiceRequest/${request.id}`,
                _sort: '-date',
              })
              .catch(() => undefined) as Promise<DiagnosticReport | undefined>,
          ]);
          return { request, orderedBy, results, diagnosticReport };
        })
      );

      setOrders(rows);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load HSAT orders'));
    } finally {
      setLoading(false);
    }
  }, [medplum, patient?.id]);

  useEffect(() => {
    fetch().catch(console.error);
  }, [fetch]);

  return { orders, loading, error, refresh: fetch };
}
